import clsx from "clsx";
import { Boxes, Package, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { HoldButton, useConfirm } from "../../web/components/Confirm";
import { FixHere } from "../../web/components/FixHere";
import { ErrorNotice, SectionCard, TONE_TEXT } from "../../web/components/ui";
import { plural } from "../../shared/words";
import { useMoney } from "../../web/lib/money";
import { can, itemName, usePriceListSaver } from "./data";
import { inUse, isProduct, type MaterialRow, type PriceListData } from "./meta";
import { ChangeLines } from "./ChangeLines";
import { usePriceListWindows } from "./windows";
import { builtToUpdate, type BuiltToUpdate, COST_PROBLEMS, itemContext, type ItemProblem, itemProblems } from "./attention";

/**
 * A product's items (built from it) that have something to look at of their own (attention.ts, as their windows' Needs attention
 * says), each with what: not their price or cost being behind their products (that's this product's to update, in its Used in),
 * and without See costs, not what only costs tell (as their lists).
 */
export function builtItemProblems(data: PriceListData, product: MaterialRow): Map<string, ItemProblem[]> {
    const out = new Map<string, ItemProblem[]>();
    if (!isProduct(product)) return out;
    const ctx = itemContext(data);
    const seesCosts = can(data, "costs.view");
    const behind = new Set(seesCosts ? builtToUpdate(product, ctx).map((u) => u.item.uuid) : []);
    for (const m of data.materials) {
        if (!inUse(m) || !data.packages[m.uuid]?.some((p) => p.uuid === product.uuid)) continue;
        const problems = itemProblems(m, ctx).filter((p) => (seesCosts || !COST_PROBLEMS.has(p.id)) && !(behind.has(m.uuid) && (p.id === "check" || p.id === "costUpdate")));
        if (problems.length) out.set(m.uuid, problems);
    }
    return out;
}

/**
 * What it's used in, each opening its window: a product, the items built from it; an item, the bundles holding it (neither
 * archived). A product's items that aren't what their products add up to now (catalogue.ts: never changed by themselves) are each
 * marked, with what they'd be and an Update of their own; more than one, Update all in its heading (asked first, listing each
 * change: each is one in ServiceM8). None: nothing. `token`: scrolled to and flashed (its items to update pill).
 */
export function UsedIn({ data, item, token }: Readonly<{ data: PriceListData; item: MaterialRow; token?: number }>) {
    const windows = usePriceListWindows();
    const fmt = useMoney();
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const bundles = data.bundles.filter((b) => b.active && b.material_list.some((l) => l.uuid === item.uuid));
    const packages = data.materials.filter((m) => inUse(m) && data.packages[m.uuid]?.some((p) => p.uuid === item.uuid));
    // What's to update is worked out from costs and pricing: only with See costs (attention.ts COST_PROBLEMS).
    const toUpdate = can(data, "costs.view") && isProduct(item) ? builtToUpdate(item, itemContext(data)) : [];
    const canUpdate = can(data, "items.pricing") && can(data, "costs.edit");
    const changeOf = new Map(toUpdate.map((u) => [u.item.uuid, u]));
    // Its items' own warnings (their names to update, item numbers...): marked on their rows, to open and deal with there.
    const problemsOf = builtItemProblems(data, item);
    const changeText = (u: BuiltToUpdate) =>
        [u.check ? `Price ${fmt.money(u.item.price)} → ${fmt.money(u.check.target)}` : "", u.cost === null ? "" : `Cost ${fmt.money(u.item.cost)} → ${fmt.money(u.cost)}`].filter(Boolean).join(" · ");
    // These (one row's, or all), as the person; any that couldn't be said once they're done.
    const update = (only: string[], key: string) => {
        setBusy(key);
        setError(null);
        saver
            .updateBuiltFrom(item.uuid, only)
            .then((r) => {
                if (r.failed) setError(new Error(`${plural(r.failed, "item")} couldn't be updated: open ${r.failed === 1 ? "it" : "each"} to try again.`));
            })
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(null));
    };
    const updateAll = async () => {
        const yes = await confirm({
            title: `Update ${plural(toUpdate.length, "item")}?`,
            message: (
                <>
                    {toUpdate.slice(0, UPDATE_LISTED).map((u) => (
                        <span key={u.item.uuid} className="block pt-1">
                            <span className="block font-medium text-ink">{itemName(data, u.item)}</span>
                            <ChangeLines price={u.check ? { from: u.item.price, to: u.check.target } : undefined} cost={u.cost === null ? undefined : { from: u.item.cost, to: u.cost }} />
                        </span>
                    ))}
                    {toUpdate.length > UPDATE_LISTED ? <span className="block pt-1">and {toUpdate.length - UPDATE_LISTED} more</span> : null}
                </>
            ),
            detail: "Each is changed in ServiceM8.",
            confirmLabel: `Update ${plural(toUpdate.length, "item")}`,
        });
        if (yes) update(toUpdate.map((u) => u.item.uuid), "all");
    };
    const rows = [
        ...packages.map((m) => {
            const change = changeOf.get(m.uuid);
            return {
                uuid: m.uuid,
                name: itemName(data, m),
                number: m.item_number,
                icon: <Package className={`size-4 shrink-0 ${TONE_TEXT.blue}`} aria-label="Item" />,
                change: change ? changeText(change) : null,
                problems: problemsOf.get(m.uuid) ?? [],
                open: () => windows.openItem(m.uuid),
            };
        }),
        ...bundles.map((b) => ({
            uuid: b.uuid,
            name: b.name.split(/\r?\n/)[0],
            number: b.item_number,
            icon: <Boxes className={`size-4 shrink-0 ${TONE_TEXT.copper}`} aria-label="Bundle" />,
            change: null,
            problems: [] as ItemProblem[],
            open: () => windows.openBundle(b.uuid),
        })),
    ];
    if (!rows.length) return null;
    const counts = [packages.length ? plural(packages.length, "item") : "", bundles.length ? plural(bundles.length, "bundle") : ""].filter(Boolean).join(", ");
    return (
        <SectionCard
            label="Used in"
            aside={
                toUpdate.length > 1 && canUpdate ? (
                    <button type="button" className="link-button" disabled={busy !== null} onClick={() => void updateAll()}>
                        {busy === "all" ? "Updating..." : `Update all ${toUpdate.length}`}
                    </button>
                ) : (
                    counts
                )
            }
            bodyClassName="divide-y divide-line"
            // Its items to update pill's Show: the card itself flashes.
            fix={<FixHere attached token={token}>{null}</FixHere>}
        >
            {rows.map((r) => (
                <div
                    key={r.uuid}
                    className={clsx("flex items-center gap-2 px-3 py-2 text-sm", r.problems.some((p) => p.danger) ? "bg-danger-soft" : r.change || r.problems.length ? "bg-warning-soft" : "hover:bg-surface-2")}
                >
                    <button type="button" className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left" onClick={r.open}>
                        {r.icon}
                        <span className="min-w-0 flex-1">
                            <span className="block truncate">{r.name}</span>
                            {r.change ? (
                                <span className="flex items-center gap-1 text-xs text-warning-ink tabular-nums">
                                    <TriangleAlert className="size-3 shrink-0" aria-label="Not what its products add up to now" />
                                    {r.change}
                                </span>
                            ) : null}
                            {/* Its own warnings, as its window's Needs attention lists them: open it to deal with them. */}
                            {r.problems.length ? (
                                <span className={clsx("flex items-center gap-1 text-xs", r.problems.some((p) => p.danger) ? "text-danger" : "text-warning-ink")}>
                                    <TriangleAlert className="size-3 shrink-0" aria-label="Needs attention" />
                                    <span className="truncate">{r.problems.map((p) => p.text).join(" · ")}</span>
                                </span>
                            ) : null}
                        </span>
                        <span className="shrink-0 text-xs text-muted">{r.number}</span>
                    </button>
                    {/* Saved to ServiceM8 straight away: held, as its own Pricing tab's Update is. */}
                    {r.change && canUpdate ? (
                        <HoldButton
                            className="btn-primary shrink-0"
                            label={busy === r.uuid ? "Updating..." : "Update"}
                            disabled={busy !== null}
                            title="Its price and cost go to what its products add up to now, saved in ServiceM8"
                            onDone={() => update([r.uuid], r.uuid)}
                        />
                    ) : null}
                </div>
            ))}
            <ErrorNotice error={error} />
        </SectionCard>
    );
}

/** At most this many of the changes are listed in Update all's question (then "and 3 more"). */
const UPDATE_LISTED = 8;
